import { Table as TableIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/cn';
import { type } from '@/theme/typography';

import { EmptyState } from './EmptyState';
import { Skeleton } from './Skeleton';

export interface Column<Row> {
  key: string;
  header: ReactNode;
  cell: (row: Row) => ReactNode;
  /** The primary column reads in `ink-900`; the rest in `ink-700` (§3 Typography). */
  primary?: boolean;
  align?: 'left' | 'right';
  className?: string;
}

interface Props<Row> {
  columns: Array<Column<Row>>;
  rows: Row[];
  rowKey: (row: Row) => string | number;
  /** When a row navigates, the WHOLE row is the target — not a link in one cell (§4). */
  onRowActivate?: (row: Row) => void;
  rowLabel?: (row: Row) => string;
  isLoading?: boolean;
  /** What to show when there are no rows. Different empties are different states (§8). */
  empty?: ReactNode;
  /**
   * Whether the table stacks into label/value rows below `lg` (§10). **On by default, and off for
   * a table nobody sees** — the `sr-only` alternative a chart carries for §9 has no width to be
   * narrow at, so making its structure depend on the viewport only means a screen reader hears a
   * different thing at 900px than at 1100px, and hears each column name from the header row *and*
   * from the per-cell label if the two ever stop being exact complements. A non-stacking table
   * keeps its real `<thead>` in the accessibility tree at every width and carries no per-cell
   * labels at all.
   */
  stacked?: boolean;
  className?: string;
}

/**
 * `DESIGN.md` §3 and §4: sticky header underlined in `rule-strong`, hairline row separators,
 * `paper-sunken` on hover, no zebra striping, and **no card around it** — "a card around a table
 * is a border around a border".
 *
 * The table owns its loading and empty states so that a screen cannot ship without them.
 *
 * ⚠️ **Below `lg` it is stacked rows, not a table** (§10). A dense table does not fit a laptop's
 * narrow half or a tablet, and it did not stack: at 640, 768 and 1000px it stayed a table with a
 * visible header, found in a live browser. Each row becomes a block and each cell a label/value
 * pair, the label being the column's own header — so **every** table in the product stacks, and a
 * screen does not have to remember to ask for it.
 *
 * The explicit `role`s are what make that safe: changing a table's `display` drops its table
 * semantics in every browser, which would leave a screen reader with rows of unrelated text. The
 * roles restore what the CSS takes away, and the per-cell labels carry the column name below `lg`,
 * where the real header row is `display: none` and cannot.
 *
 * ⚠️ **An activatable row keeps `role="row"`; it is not a `link`.** It shipped as
 * `role={interactive ? 'link' : 'row'}`, which ARIA reads as a link with *presentational children*
 * — so every `cell` beneath it is discarded, the column headers stop being associated with the
 * values under them, and `<tbody role="rowgroup">` owns something that is not a row. The tree
 * cannot be both a table and a link, and §9 asks for the table: without it a stacked row is the
 * "rows of unrelated text" the roles exist to prevent. The row stays keyboard-operable (`tabIndex`,
 * Enter and Space, a visible focus ring) and carries `rowLabel` as its accessible name.
 *
 * What that costs is the *announcement* — a focused row is not read as "link", so nothing tells a
 * screen-reader user it can be activated. The fix for that is a real `<a href>` in the primary
 * cell, which the whole row's click then mirrors for the mouse; it needs a URL, and this component
 * is given a callback. **§6.3's candidate table is the first one with a URL to give** — it wires
 * rows to `/candidates/:id`, and that is where the anchor belongs, not here.
 */
export function Table<Row>({
  columns,
  rows,
  rowKey,
  onRowActivate,
  rowLabel,
  isLoading = false,
  empty,
  stacked = true,
  className,
}: Props<Row>) {
  const { t } = useTranslation('components');

  if (isLoading) {
    return (
      <div className={cn('border-y border-rule bg-paper', className)} aria-busy="true">
        <span className="sr-only">{t('table.loading')}</span>
        {Array.from({ length: 5 }, (_, index) => (
          <div key={index} className="border-b border-rule px-3 py-2.5 last:border-b-0">
            <Skeleton className="h-4 w-full" />
          </div>
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return <>{empty ?? <EmptyState icon={TableIcon} message={t('table.empty')} />}</>;
  }

  return (
    <table
      role="table"
      className={cn(
        'w-full border-y border-rule bg-paper text-left',
        stacked && 'block lg:table',
        className,
      )}
    >
      <thead
        role="rowgroup"
        className={cn('sticky top-0 z-10 bg-paper', stacked && 'hidden lg:table-header-group')}
      >
        <tr role="row" className="border-b border-rule-strong">
          {columns.map((column) => (
            <th
              key={column.key}
              role="columnheader"
              scope="col"
              className={cn(
                'px-3 py-2.5',
                type.eyebrow,
                column.align === 'right' && 'text-right',
                column.className,
              )}
            >
              {column.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody role="rowgroup" className={cn(stacked && 'block lg:table-row-group')}>
        {rows.map((row) => {
          const interactive = Boolean(onRowActivate);
          return (
            <tr
              key={rowKey(row)}
              role="row"
              className={cn(
                'border-b border-rule transition-motion last:border-b-0',
                stacked && 'block py-2 lg:table-row lg:py-0',
                interactive &&
                  'cursor-pointer hover:bg-paper-sunken focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
              )}
              {...(interactive
                ? {
                    tabIndex: 0,
                    'aria-label': rowLabel?.(row),
                    onClick: () => onRowActivate?.(row),
                    onKeyDown: (event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onRowActivate?.(row);
                      }
                    },
                  }
                : {})}
            >
              {columns.map((column) => (
                /*
                 * ⚠️ **`[overflow-wrap:anywhere]` is what keeps one long value from moving the
                 * page.** A cell had no break rule at all, so a value with no break opportunity —
                 * an email, which is what Section 1 and Section 5 are full of — set the row's
                 * min-content width and dragged the whole document sideways with it. Measured in a
                 * live browser at a 390px viewport: `documentElement.scrollWidth` went to 420 for a
                 * 39-character corporate address, 495 for 52, and 971 for a 90-character name,
                 * which left the layout anchored in a wide empty canvas with the value running off
                 * screen. At 1440 the same name took the document to 1746. Ordinary data, not a
                 * torture case: it is invisible today only because the seed emails are short.
                 *
                 * `anywhere` rather than `break-words`/`overflow-wrap: break-word`, and the
                 * difference is the whole fix: only `anywhere` counts those break opportunities
                 * when the **intrinsic min-content width** is computed, which is the number auto
                 * table layout sizes the column from and the number `min-width: auto` gives a flex
                 * item. With `break-word` the text wraps but the column still demands the longest
                 * word, and the page still scrolls. Unlike `break-all` it keeps breaking at spaces
                 * first, so ordinary multi-word text is not chopped mid-word.
                 *
                 * It sits on the `<td>` and not on the value because `overflow-wrap` inherits and
                 * `column.cell(row)` is arbitrary content this component does not own — every table
                 * in the product inherits the fix instead of each call site remembering it.
                 * `min-w-0` is the flex half: below `lg` this cell is the flex container, and a
                 * flex item's automatic minimum size is the other way a long word refuses to
                 * shrink.
                 *
                 * jsdom computes no layout and evaluates no media queries, so `Table.test.tsx`
                 * guards the classes and nothing more; the widths above were verified by
                 * `ui-reviewer` in a real browser and only there.
                 */
                <td
                  key={column.key}
                  role="cell"
                  className={cn(
                    'px-3 text-sm [overflow-wrap:anywhere]',
                    stacked
                      ? 'flex min-w-0 items-baseline justify-between gap-4 py-1 lg:table-cell lg:py-2.5'
                      : 'py-2.5',
                    column.primary ? 'text-ink-900' : 'text-ink-700',
                    column.align === 'right' && 'lg:text-right',
                    column.className,
                  )}
                >
                  {/*
                   * The column's name, where the header row cannot reach: stacked, below `lg`.
                   * The two are exact complements — `thead` is `hidden lg:table-header-group`, this
                   * is `lg:hidden` — so exactly one of them names the column at any width. A table
                   * that does not stack never renders this at all and always has the real header.
                   *
                   * ⚠️ **`[overflow-wrap:normal]`, because the cell's own rule is for a *value* and
                   * this is a column name** (a `ui-reviewer` live pass, 2026-08-12: at a 390px
                   * viewport the `SET` label of §6.9's longest set name rendered as `SE` / `T`).
                   * `anywhere` inherits from the `<td>`, where it is what keeps one long value from
                   * dragging the document sideways; here it lets a three-letter word be broken by a
                   * value squeezing the flex line. This restores the default — ordinary breaking at
                   * spaces, so *"Active questions"* still wraps onto two lines — and it changes
                   * nothing about the value beside it, which keeps `anywhere` and the measurements
                   * that argued for it. It is §6.9's *"the state column does not wrap"* one level up:
                   * the exception belongs to the text that is always a word rather than data.
                   *
                   * ⚠️ **The separator inside it is the whole of BUG-105, and it is about the
                   * accessible name rather than the layout** (2026-09-25). This span sat directly
                   * against `column.cell(row)` with nothing between them, and a cell's name is
                   * computed from its contents: the two ran together in **every** stacked cell of
                   * **every** table in the product — *"Written byAnna Admin"*, *"StateActive"*,
                   * *"Active questions5 questions"*, a digit fused to a word and two words fused
                   * into a non-word. A sighted reader never meets it, because the flex column above
                   * already puts the label on its own line; a screen-reader user meets it on every
                   * row at every width below `lg`, where `thead` is `display:none` and cannot
                   * associate a header with anything.
                   *
                   * So the separator is `sr-only`: nothing on screen changes, which is the point —
                   * a visible colon would be a design change to every table for a defect no sighted
                   * reader has. It is a **colon and a space**, and neither half is decoration. The
                   * colon is what a synthesizer turns into a *pause*, which is the boundary the line
                   * break carries visually; at the default punctuation level it is not spoken, and
                   * at *all* it is spoken along with everything else the reader asked for. The space
                   * is the fallback for the level where punctuation is dropped entirely — *"Active
                   * questions 5 questions"* is at least a sequence of words, while *"Active
                   * questions5 questions"* is not. An `aria-label` on the `<td>` was the other
                   * candidate and is unavailable here for a structural reason rather than a stylistic
                   * one: `column.cell(row)` is arbitrary `ReactNode` this component does not own, so
                   * there is no string to compose one from, and a name set on the cell would replace
                   * whatever a badge, a link or a menu inside it says for itself.
                   *
                   * It is an element **beside** the label rather than text inside it, and that is
                   * not arbitrary: `data-column-label` marks *the column's name*, and screens across
                   * the product read it — as an element whose text is the header (`getByText`), and
                   * twice as a prefix whose length is sliced off a cell to leave the value. A colon
                   * folded into it would make the marked element hold something that is not the
                   * column's name. It carries `lg:hidden` for the same reason the label does, so a
                   * desktop cell is not prefixed with a stray colon once the real header is back.
                   *
                   * It costs no layout: `sr-only` is `position: absolute`, and an absolutely
                   * positioned child of a flex container is not a flex item, so the stacked cell
                   * still lays out as exactly two things.
                   *
                   * ⚠️ jsdom computes no layout and evaluates no media queries, so the suite can
                   * assert the string a cell is composed of and nothing about what is announced. A
                   * real screen reader on a narrow viewport is the only place this is verified.
                   */}
                  {stacked && (
                    <>
                      <span
                        data-column-label
                        className={cn(type.eyebrow, '[overflow-wrap:normal] lg:hidden')}
                      >
                        {column.header}
                      </span>
                      <span data-column-separator className="sr-only lg:hidden">
                        {t('table.labelSeparator')}
                      </span>
                    </>
                  )}
                  {column.cell(row)}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
